import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type HTMLAttributes,
  type Ref,
} from "react";
import { LiquidGlassFilter } from "./LiquidGlassFilter";
import {
  createGlassMap,
  readGlassRect,
  supportsGlassRefraction,
  type GlassMap,
} from "./liquidGlassOptics";
import { createGlassGroupStore, LiquidGlassGroupContext } from "./liquidGlassGroupContext";
import { useVisualEffects } from "./useVisualEffects";
import "./VisualEffects.css";

export type LiquidGlassGroupProps = HTMLAttributes<HTMLElement> & {
  as?: "div" | "header" | "nav";
  adaptation?: "auto" | "none";
  clear?: boolean;
  /** Bevel blending distance in CSS pixels. Nearby surfaces join below half this gap. */
  fusion?: number;
  ref?: Ref<HTMLElement>;
};

function visibleMember(member: HTMLElement, root: HTMLElement) {
  if (!member.getClientRects().length || getComputedStyle(member).visibility !== "visible") return false;
  // Fading members retain their own material so ancestor opacity applies correctly.
  for (let element: HTMLElement | null = member; element && element !== root; element = element.parentElement) {
    if (Number(getComputedStyle(element).opacity) < 1) return false;
  }
  return true;
}

const geometryProperty = /transform|width|height|gap|padding|margin|left|top|right|bottom|flex|grid|border-radius|opacity|visibility/;

export function LiquidGlassGroup({
  as: Component = "div",
  adaptation = "auto",
  children,
  className = "",
  clear = false,
  fusion = 24,
  ref,
  style,
  ...props
}: LiquidGlassGroupProps) {
  const { level, reducedMotion, reducedTransparency } = useVisualEffects();
  const [capable] = useState(supportsGlassRefraction);
  const [store] = useState(createGlassGroupStore);
  const active = useSyncExternalStore(store.subscribe, () => store.active, () => false);
  const [map, setMap] = useState<GlassMap>();
  const root = useRef<HTMLElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const id = `ark-liquid-group-${useId().replace(/:/g, "")}`;
  const blend = Number.isFinite(fusion) ? Math.max(0, Math.min(64, fusion)) : 24;
  const padding = blend / 4 + 2;
  const enabled = level === "rich" && !reducedTransparency && capable;
  const attach = useCallback((element: HTMLElement | null) => {
    root.current = element;
    store.setRoot(element);
    store.schedule();
    if (typeof ref === "function") return ref(element);
    if (ref) ref.current = element;
  }, [ref, store]);

  useEffect(() => {
    const element = root.current;
    store.setActive(false);
    if (!enabled || !element || typeof ResizeObserver === "undefined") return;
    let frame = 0;
    let trackingUntil = 0;
    let motions: { target: Element; key: string; until: number }[] = [];
    let lastRead = 0;
    let signature = "";
    let materialized = false;
    const observed = new Set<HTMLElement>();
    const round = (value: number) => Math.round(value * 2) / 2;
    const update = (now: number) => {
      frame = 0;
      // Geometry animations are sampled at 30fps, never in an idle frame loop.
      if (now - lastRead < 32) {
        frame = requestAnimationFrame(update);
        return;
      }
      lastRead = now;
      const bounds = element.getBoundingClientRect();
      const candidates = [...store.members].filter((member) =>
        element.contains(member) &&
        member.closest(".ark-liquid-group") === element,
      );
      for (const member of observed) {
        if (!candidates.includes(member)) {
          observer.unobserve(member);
          observed.delete(member);
        }
      }
      for (const member of candidates) {
        if (!observed.has(member)) {
          observed.add(member);
          observer.observe(member);
        }
      }
      const members = candidates.filter((member) => visibleMember(member, element));
      // Very large collections retain individual surfaces rather than hiding controls.
      const geometry = members.length <= 32 ? members.map((member) => {
        const box = member.getBoundingClientRect();
        const rect = readGlassRect(member,
          round(box.left - bounds.left + padding),
          round(box.top - bounds.top + padding));
        return { member, shape: { ...rect, width: round(rect.width), height: round(rect.height),
          radii: rect.radii.map(round) } };
      }).filter(({ shape }) => shape.width >= 1 && shape.height >= 1) : [];
      const shapes = geometry.map(({ shape }) => shape);
      const width = bounds.width + padding * 2;
      const height = bounds.height + padding * 2;
      const nextSignature = JSON.stringify([width, height, shapes]);
      if (nextSignature !== signature) {
        signature = nextSignature;
        const next = shapes.length ? createGlassMap(width, height, shapes, blend, true) : undefined;
        setMap(next);
        materialized = Boolean(next);
      }
      store.setActive(materialized, materialized ? geometry.map(({ member }) => member) : []);
      if (now < trackingUntil) frame = requestAnimationFrame(update);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    store.setSchedule(schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    const mutations = new MutationObserver((records) => {
      if (records.some((record) =>
        record.target instanceof Element &&
        !["data-fusion", "data-glass-grouped", "data-refraction"].includes(record.attributeName ?? "") &&
        !record.target.closest("[data-glass-decoration]"))) schedule();
    });
    // Application data/ARIA attributes can also change CSS geometry without a transition.
    mutations.observe(element, { attributes: true, childList: true, subtree: true });
    const start = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element) ||
        ((event instanceof TransitionEvent || event instanceof AnimationEvent) && event.pseudoElement) ||
        ![...store.members].some((member) =>
          element.contains(member) && member.closest(".ark-liquid-group") === element &&
          (target === member || target.contains(member)))) return;
      const property = event instanceof TransitionEvent ? event.propertyName : "transform";
      if (!geometryProperty.test(property)) return;
      if (!reducedMotion) {
        const computed = getComputedStyle(target);
        const times = `${computed.transitionDuration},${computed.transitionDelay},${computed.animationDuration},${computed.animationDelay}`
          .split(",").map((value) => parseFloat(value) * (value.trim().endsWith("ms") ? 1 : 1000));
        const key = event instanceof TransitionEvent ? `transition:${property}` :
          event instanceof AnimationEvent ? `animation:${event.animationName}` : "geometry";
        motions = motions.filter((motion) => motion.target !== target || motion.key !== key);
        motions.push({ target, key, until: performance.now() +
          Math.min(2_000, Math.max(...times, 0) * 2 + 64) });
        trackingUntil = Math.max(...motions.map((motion) => motion.until), 0);
      }
      schedule();
    };
    const end = (event: Event) => {
      if (event instanceof TransitionEvent && !geometryProperty.test(event.propertyName)) return;
      if ((event instanceof TransitionEvent || event instanceof AnimationEvent) && event.pseudoElement) return;
      const key = event instanceof TransitionEvent ? `transition:${event.propertyName}` :
        event instanceof AnimationEvent ? `animation:${event.animationName}` : "geometry";
      const previous = motions.length;
      motions = motions.filter((motion) => motion.target !== event.target || motion.key !== key);
      if (motions.length === previous) return;
      // A shorter transition ending must not stop another member's ongoing motion.
      trackingUntil = Math.max(...motions.map((motion) => motion.until), 0);
      schedule();
    };
    element.addEventListener("transitionstart", start);
    element.addEventListener("animationstart", start);
    element.addEventListener("transitionend", end);
    element.addEventListener("transitioncancel", end);
    element.addEventListener("animationend", end);
    element.addEventListener("animationcancel", end);
    element.addEventListener("scroll", schedule, { capture: true, passive: true });
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      mutations.disconnect();
      store.setSchedule(() => {});
      store.setActive(false);
      element.removeEventListener("transitionstart", start);
      element.removeEventListener("animationstart", start);
      element.removeEventListener("transitionend", end);
      element.removeEventListener("transitioncancel", end);
      element.removeEventListener("animationend", end);
      element.removeEventListener("animationcancel", end);
      element.removeEventListener("scroll", schedule, true);
    };
  }, [Component, blend, enabled, padding, reducedMotion, store]);

  useEffect(() => {
    const element = root.current;
    const material = surface.current;
    if (!element || !material || !enabled || !active || reducedMotion) return;
    let frame = 0;
    const move = (event: PointerEvent) => {
      if (event.pointerType === "touch" ||
        !(event.target instanceof Element) ||
        event.target.closest(".ark-liquid-group") !== element) return;
      cancelAnimationFrame(frame);
      const { left, top } = material.getBoundingClientRect();
      const x = event.clientX - left;
      const y = event.clientY - top;
      frame = requestAnimationFrame(() => {
        material.style.setProperty("--ark-pointer-x", `${x}px`);
        material.style.setProperty("--ark-pointer-y", `${y}px`);
      });
    };
    const leave = () => {
      cancelAnimationFrame(frame);
      material.style.removeProperty("--ark-pointer-x");
      material.style.removeProperty("--ark-pointer-y");
    };
    element.addEventListener("pointermove", move, { passive: true });
    element.addEventListener("pointerleave", leave);
    return () => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerleave", leave);
      leave();
    };
  }, [Component, active, enabled, reducedMotion]);

  return (
    <LiquidGlassGroupContext.Provider value={store}>
      <Component {...props} className={`ark-liquid-group ${className}`}
        data-fusion={enabled && active && map ? "true" : undefined} ref={attach} style={style}>
        {enabled && active && map ? (
          <div aria-hidden="true"
            className={`ark-glass ark-liquid ark-liquid-group-surface ${clear ? "ark-glass-clear" : ""}`}
            ref={surface}
            data-adaptation={adaptation} data-glass-decoration="" data-refraction="true"
            style={{ "--ark-refraction": `url("#${id}")`,
              "--ark-liquid-shape": `url("${map.source}")`,
              height: map.height, left: -padding, top: -padding, width: map.width } as CSSProperties}>
            <LiquidGlassFilter adaptive={adaptation === "auto"} id={id} map={map} />
          </div>
        ) : null}
        {children}
      </Component>
    </LiquidGlassGroupContext.Provider>
  );
}
